import { FileLoader, Group, Mesh, MeshBasicMaterial, PlaneGeometry } from 'three';

import type { CoordConfig, LoadStatus, Rawdata, RawFileList } from '@/types';

// 文件渲染展示参数
export type RawObjectTheme = 'origin' | 'algor' | 'binarization' | 'height' | 'intensity';

export type RawObjectOptions = {
  /** 主题 */
  colorTheme?: RawObjectTheme;
  /** 点云的点大小 */
  pointSize?: number;
  /** 点云z轴可视范围 */
  visibleZ?: [number, number];
  /** 点云地平线高度 */
  groundZ?: number;
  /** 材质值二值化分割值 */
  intensitySeg?: number;
  // 按高度着色
  heightSeg?: [number, number];
  /** 点云透明度（不可配置，叠帧显示用的） */
  opacity?: number;
};

export interface RawLoadOptions {
  coord: Required<CoordConfig>;
  onProgress?: (data: LoadStatus) => void;
}

export const RAW_THEME_MAP: Record<RawObjectTheme, string> = {
  origin: '原色',
  algor: '智能着色',
  binarization: '双色',
  height: '高度',
  intensity: '反射强度',
};

export abstract class RawObject {
  public name: Rawdata['name'];
  public type: Rawdata['type'];
  public object: Group | Mesh<PlaneGeometry, MeshBasicMaterial>;
  public options: Required<RawObjectOptions>;

  constructor(name: Rawdata['name'], type: Rawdata['type']) {
    this.name = name;
    this.type = type;
    this.object = new Group();

    this.options = {
      colorTheme: 'algor',
      pointSize: 1,
      visibleZ: [-99, 999],
      groundZ: 0,
      intensitySeg: 1,
      heightSeg: [-99, 999],
      opacity: 1,
    };
  }

  static async resolveRawUrl(data: Rawdata) {
    let urls = [
      {
        url: data.url,
        size: data.size,
      },
    ];
    if (data.format === 'filelist') {
      const loader = new FileLoader();
      loader.setResponseType('json');
      const { files } = (await loader.loadAsync(data.url)) as unknown as RawFileList;

      if (!Array.isArray(files) || files.length === 0) {
        throw new Error('Invalid File List');
      }

      urls = files;
    }

    return urls;
  }

  abstract load(
    data: Rawdata,
    options: RawLoadOptions,
  ): Promise<{ cameraPosition?: [number, number, number] } | undefined>;

  abstract updateRenderOptions(options: RawObjectOptions, forceUpdate?: boolean): void;
}
